import { createRoot, Root } from 'react-dom/client'
import SnackBarMui from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import { AlertColor } from '@mui/lab'
import { ERROR_CODE_MESSAGES, GENERIC_ERROR, isErrorCode } from '@common/authErrorMessages'

// Only one snackbar may be live at a time. Every call mounts a fixed-position
// toast at the same top/center anchor, so two concurrent calls (e.g. an axios
// interceptor plus a page-level catch) render on top of each other and the
// texts overlap. A new call therefore replaces the current toast.
let activeDiv: HTMLDivElement | null = null
let activeRoot: Root | null = null
let activeTimer: ReturnType<typeof setTimeout> | null = null

const removeSnackBar = () => {
  if (activeTimer) {
    clearTimeout(activeTimer)
    activeTimer = null
  }
  activeRoot?.unmount()
  activeRoot = null
  activeDiv?.remove()
  activeDiv = null
}

export default function SnackBar({
  msg = '',
  duration = 2000,
  type = 'error',
}: {
  msg: string | any
  duration?: number
  type?: AlertColor
}) {
  const raw = msg.message || msg
  if (raw === '') return
  // Raw backend codes must never be displayed. Known codes translate to
  // friendly copy; an unmapped code keeps a live toast (the interceptor's
  // descriptive message) instead of replacing it, or falls back to generic
  // copy when nothing is showing.
  let message = (typeof raw === 'string' && ERROR_CODE_MESSAGES[raw]) || raw
  if (isErrorCode(message)) {
    if (activeRoot) return
    message = GENERIC_ERROR
  }
  removeSnackBar()
  const root = document.getElementById('root')
  if (!root) return
  activeDiv = document.createElement('div')
  root.appendChild(activeDiv)
  activeRoot = createRoot(activeDiv)
  activeRoot.render(
    <SnackBarMui open anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={duration}>
      <Alert severity={type}>{message}</Alert>
    </SnackBarMui>
  )
  activeTimer = setTimeout(removeSnackBar, duration)
}
